export enum SocialPlatform {
  Facebook = 'facebook',
  Instagram = 'instagram',
  LinkedIn = 'linkedIn',
  Twitter = 'twitter',
}

type PlatformConfig = Record<
  SocialPlatform,
  {
    canonicalUrl: string;
    domains: RegExp[];
    pathPrefix?: string;
    profileIdPattern?: string;
  }
>;

const PROFILE_ID_PATTERN = '[\\w.\\-]+';
const PROFILE_ID_PATTERN_UNICODE = '[\\w.\\-\\p{Letter}\\p{Mark}]+';
const FACEBOOK_PROFILE_PHP_PATH = /\/profile\.php/iu;
const FACEBOOK_PROFILE_PHP_PATTERN = /(?:facebook\.com|fb\.com)\/profile\.php\?id=\d+/iu;
const FACEBOOK_PEOPLE_PATTERN = /(?:facebook\.com|fb\.com)\/people\/[^/]+\/\d+\/?(?:\?.*)?$/iu;
const TWITTER_INTENT_PATTERN = /(?:twitter\.com|x\.com)\/intent\/user\?screen_name=[\w.]+/iu;

const PLATFORM_CONFIG: PlatformConfig = {
  facebook: {
    canonicalUrl: 'https://facebook.com/',
    domains: [
      /^(https?:\/\/)?(www\.)?facebook\.com/u,
      /^(https?:\/\/)?m\.facebook\.com/u,
      /^(https?:\/\/)?mobile\.facebook\.com/u,
      /^(https?:\/\/)?web\.facebook\.com/u,
      /^(https?:\/\/)?touch\.facebook\.com/u,
      /^(https?:\/\/)?(www\.)?fb\.com/u,
      /^(https?:\/\/)?(www\.)?fb\.me/u,
      /^(https?:\/\/)?0\.facebook\.com/u,
      /^(https?:\/\/)?mbasic\.facebook\.com/u,
      /^(https?:\/\/)?[a-z]{2}(-[a-z]{2})?\.facebook\.com/u,
    ],
  },
  instagram: {
    canonicalUrl: 'https://instagram.com/',
    domains: [
      /^(https?:\/\/)?(www\.)?instagram\.com/u,
      /^(https?:\/\/)?m\.instagram\.com/u,
      /^(https?:\/\/)?(www\.)?instagr\.am/u,
      /^(https?:\/\/)?(www\.)?ig\.me/u,
    ],
  },
  linkedIn: {
    canonicalUrl: 'https://linkedin.com/in/',
    domains: [/^(https?:\/\/)?([a-z]{1,3}\.)?linkedin\.com/u],
    pathPrefix: '/in/',
    profileIdPattern: PROFILE_ID_PATTERN_UNICODE,
  },
  twitter: {
    canonicalUrl: 'https://x.com/',
    domains: [
      /^(https?:\/\/)?(www\.)?x\.com/u,
      /^(https?:\/\/)?(www\.)?twitter\.com/u,
      /^(https?:\/\/)?mobile\.twitter\.com/u,
      /^(https?:\/\/)?m\.twitter\.com/u,
      /^(https?:\/\/)?mobile\.x\.com/u,
      /^(https?:\/\/)?m\.x\.com/u,
    ],
  },
};

const isUrl = (value: string) => /^https?:\/\//u.test(value) || /^[\w.-]+\.\w+/u.test(value);

const stripQueryParams = (url: string): string => url.replace(/\?.*$/u, '');

const getProfileIdPattern = (platform: SocialPlatform): string =>
  PLATFORM_CONFIG[platform].profileIdPattern ?? PROFILE_ID_PATTERN;

const buildUrlPattern = (platform: SocialPlatform): RegExp[] => {
  const config = PLATFORM_CONFIG[platform];
  const pathPrefix = config.pathPrefix ?? '/';
  const idPattern = getProfileIdPattern(platform);

  return config.domains.map(
    (domain) => new RegExp(`${domain.source}${pathPrefix}@?(?:${idPattern})\\/?$`, 'iu')
  );
};

const isHandleOrBareId = (value: string, platform: SocialPlatform): boolean => {
  const idPattern = getProfileIdPattern(platform);
  const handlePattern = new RegExp(`^@?${idPattern}$`, 'u');
  return handlePattern.test(value);
};

const matchesSpecialUrl = (value: string, platform: SocialPlatform): boolean => {
  if (platform === SocialPlatform.Facebook) {
    return FACEBOOK_PROFILE_PHP_PATTERN.test(value) || FACEBOOK_PEOPLE_PATTERN.test(value);
  }

  if (platform === SocialPlatform.Twitter) {
    return TWITTER_INTENT_PATTERN.test(value);
  }

  return false;
};

const matchesPlatformUrl = (value: string, platform: SocialPlatform): boolean => {
  const stripped = stripQueryParams(value);
  if (FACEBOOK_PROFILE_PHP_PATH.test(stripped) && !FACEBOOK_PROFILE_PHP_PATTERN.test(value))
    return false;

  return (
    buildUrlPattern(platform).some((pattern) => pattern.test(stripped)) ||
    matchesSpecialUrl(value, platform)
  );
};

const matchesOtherPlatformUrl = (value: string, platform: SocialPlatform): boolean =>
  (Object.keys(PLATFORM_CONFIG) as SocialPlatform[])
    .filter((p) => p !== platform)
    .some((p) => PLATFORM_CONFIG[p].domains.some((domain) => domain.test(value)));

const extractProfileId = (value: string, platform: SocialPlatform): null | string => {
  const config = PLATFORM_CONFIG[platform];
  const pathPrefix = config.pathPrefix ?? '/';
  const idPattern = getProfileIdPattern(platform);

  for (const domain of config.domains) {
    const match = new RegExp(
      `${domain.source}${pathPrefix}@?(?<profileId>${idPattern})\\/?$`,
      'iu'
    ).exec(value);

    if (match?.groups?.['profileId']) return match.groups['profileId'];
  }

  const handleMatch = new RegExp(`^@?(?<profileId>${idPattern})$`, 'u').exec(value);
  return handleMatch?.groups?.['profileId'] ?? null;
};

/**
 * Normalizes a social media profile input to a canonical URL.
 * Handles bare usernames, @handles, and any valid platform URL variant.
 * Returns the canonical URL or the original value if it can't be normalized.
 */
export const formatSocialLink = (value: string, platform: SocialPlatform): string => {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;

  if (isUrl(trimmed) && matchesPlatformUrl(trimmed, platform)) {
    return /^https?:\/\//u.test(trimmed) ? trimmed : `https://${trimmed}`;
  }

  const profileId = extractProfileId(trimmed, platform);
  if (!profileId) return trimmed;

  return `${PLATFORM_CONFIG[platform].canonicalUrl}${profileId}`;
};

/**
 * Validates a social media profile input for a given platform.
 * Accepts either a handle (@username / username) or a full profile URL for that platform.
 * Returns an error message string if invalid, or `true` if valid.
 */
export const validateSocialLink = (
  platform: SocialPlatform,
  errorMessage: string
): ((value: unknown) => string | true) => {
  return (value: unknown) => {
    if (typeof value !== 'string' || !value) return true;

    const trimmed = value.trim();
    if (!trimmed) return true;

    if (isHandleOrBareId(trimmed, platform)) return true;

    if (isUrl(trimmed)) {
      if (matchesPlatformUrl(trimmed, platform)) return true;
      return errorMessage;
    }

    if (matchesOtherPlatformUrl(trimmed, platform)) {
      return errorMessage;
    }

    return errorMessage;
  };
};
